import Logo from "./logo";

const YOUTUBE_URL = "https://www.youtube.com/";
const YOUTUBE_MUSIC_URL = "https://music.youtube.com/";
const SOUNDCLOUD_URL = "https://soundcloud.com/";

function formatMinutes(minutes) {
  const value = Math.max(0, Math.floor(Number(minutes) || 0));
  return value >= 60
    ? `${Math.floor(value / 60)}h ${value % 60}m`
    : `${value} min`;
}

// What the plan card says, from the same props the header reads. Slowing down
// is free at any meter reading, so the free lines never imply it stops.
export function describePlan({
  isPro,
  trialMode,
  trialEnded,
  remainingTrialDays,
  usageHit,
  usageRemaining,
  usagePeriod,
}) {
  if (isPro) {
    return { title: "Slowed Pro", detail: "Every effect, mood and skin." };
  }
  if (trialMode && remainingTrialDays >= 0) {
    return {
      title: "Pro trial",
      detail:
        remainingTrialDays > 0
          ? `${remainingTrialDays} ${remainingTrialDays === 1 ? "day" : "days"} left`
          : "Last day",
    };
  }
  if (trialEnded === true) {
    const resets = usagePeriod === "monthly" ? "resets monthly" : "resets weekly";
    return {
      title: "Free plan",
      detail: usageHit
        ? `Pro minutes used up, ${resets}`
        : `${formatMinutes(usageRemaining)} of Pro left, ${resets}`,
    };
  }
  return { title: "Free plan", detail: "Slowing down is always free." };
}

// On SoundCloud before Slowed has been allowed to run there. Chrome asks the
// listener itself; this is the click that lets it.
function SoundCloudAccess({ busy, error, onEnableSoundCloudClick }) {
  return (
    <>
      <h1 className="unsupported-gate__title">Turn on Slowed for SoundCloud</h1>
      <p className="unsupported-gate__text">
        Chrome will ask to let Slowed run on SoundCloud and its player. The
        page reloads once, then click Slowed again.
      </p>
      {error && (
        <p className="unsupported-gate__text" role="alert">
          {error}
        </p>
      )}
      <div className="unsupported-gate__ctas">
        <button
          type="button"
          className="unsupported-gate__cta unsupported-gate__cta--primary"
          disabled={busy}
          aria-busy={busy}
          onClick={() => onEnableSoundCloudClick?.()}
        >
          Turn on for SoundCloud
        </button>
      </div>
    </>
  );
}

// Where the floating panel cannot mount, the native popup shows this instead
// of the controls: a way to a site Slowed runs on, the plan, and the links
// that don't need a player (feedback, help, billing).
export default function UnsupportedGate({
  isPro,
  trialMode,
  trialEnded,
  remainingTrialDays,
  usageHit,
  usageRemaining,
  usagePeriod,
  hideManagePlan,
  billingLoading,
  upgradeError,
  isFeedbackEnabled,
  isRateAppEnabled,
  toolUrl,
  onOpenToolClick,
  onOpenYouTubeClick,
  soundCloudNeedsAccess,
  studioIdle,
  soundCloudAccessBusy,
  soundCloudAccessError,
  onEnableSoundCloudClick,
  onUpgradeClick,
  onManageSubscriptionClick,
  onFeedbackClick,
  onHelpClick,
  onChangelogClick,
  onRateAppClick,
}) {
  const plan = describePlan({
    isPro,
    trialMode,
    trialEnded,
    remainingTrialDays,
    usageHit,
    usageRemaining,
    usagePeriod,
  });
  const feedbackEnabled = isFeedbackEnabled !== false;
  const links = [
    feedbackEnabled && {
      key: "feedback",
      label: "Share feedback",
      onClick: () => onFeedbackClick?.("general"),
    },
    feedbackEnabled && {
      key: "bug",
      label: "Report a bug",
      onClick: () => onFeedbackClick?.("bug"),
    },
    { key: "faq", label: "FAQ", onClick: () => onHelpClick?.() },
    { key: "changelog", label: "What’s new", onClick: () => onChangelogClick?.() },
    isRateAppEnabled !== false && {
      key: "rate",
      label: "Rate Slowed",
      onClick: () => onRateAppClick?.(),
    },
  ].filter(Boolean);

  return (
    <div className="unsupported-gate">
      <div className="unsupported-gate__brand" aria-label="Slowed">
        <Logo isPro={isPro} />
      </div>

      <div className="unsupported-gate__hero">
        <span className="unsupported-gate__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22">
            <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
          </svg>
        </span>
        {soundCloudNeedsAccess ? (
          <SoundCloudAccess
            busy={soundCloudAccessBusy === true}
            error={soundCloudAccessError}
            onEnableSoundCloudClick={onEnableSoundCloudClick}
          />
        ) : studioIdle ? (
          <>
            <h1 className="unsupported-gate__title">
              Play a song in Slowed Studio
            </h1>
            <p className="unsupported-gate__text">
              Paste a YouTube or SoundCloud link in Studio and play it, then
              click Slowed again: these controls work on it here too.
            </p>
          </>
        ) : (
          <>
            <h1 className="unsupported-gate__title">
              Slowed works on YouTube and SoundCloud
            </h1>
            <p className="unsupported-gate__text">
              Open a video on YouTube or a track on SoundCloud, then click
              Slowed again.
            </p>
            <div className="unsupported-gate__ctas">
              <button
                type="button"
                className="unsupported-gate__cta unsupported-gate__cta--primary"
                onClick={() => onOpenYouTubeClick?.(YOUTUBE_URL)}
              >
                Open YouTube
              </button>
              <button
                type="button"
                className="unsupported-gate__cta"
                onClick={() => onOpenYouTubeClick?.(YOUTUBE_MUSIC_URL)}
              >
                Open YouTube Music
              </button>
              <button
                type="button"
                className="unsupported-gate__cta"
                onClick={() => onOpenYouTubeClick?.(SOUNDCLOUD_URL)}
              >
                Open SoundCloud
              </button>
              {toolUrl && (
                <button
                  type="button"
                  className="unsupported-gate__cta unsupported-gate__cta--tool"
                  onClick={() => onOpenToolClick?.()}
                >
                  Open Slowed Studio
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="unsupported-gate__plan">
        <div className="unsupported-gate__plan-text">
          <span className="unsupported-gate__plan-title">{plan.title}</span>
          <span className="unsupported-gate__plan-detail">{plan.detail}</span>
          {upgradeError && (
            <span className="unsupported-gate__plan-error" role="alert">
              {upgradeError}
            </span>
          )}
        </div>
        {isPro ? (
          !hideManagePlan && (
            <button
              type="button"
              className="unsupported-gate__plan-button"
              disabled={billingLoading}
              aria-busy={billingLoading}
              onClick={() => onManageSubscriptionClick?.()}
            >
              {billingLoading ? "Loading…" : "Manage plan"}
            </button>
          )
        ) : (
          <button
            type="button"
            className="unsupported-gate__plan-button unsupported-gate__plan-button--unlock"
            onClick={() => onUpgradeClick?.()}
          >
            Unlock PRO
          </button>
        )}
      </div>

      <nav className="actions unsupported-gate__links" aria-label="Help">
        <div className="actions__list">
          {links.map((link) => (
            <a
              key={link.key}
              href="#!"
              onClick={(event) => {
                event.preventDefault();
                link.onClick();
              }}
            >
              {link.label}
            </a>
          ))}
        </div>
      </nav>
    </div>
  );
}
